'use client';

import * as React from 'react';
import { composeRefs } from '@radix-ui/react-compose-refs';

type SlotProps = React.HTMLAttributes<HTMLElement> & {
  children?: React.ReactNode;
};

const Slot = React.forwardRef<HTMLElement, SlotProps>(
  ({ children, ...slotProps }, forwardedRef) => {
    const childrenArray = React.Children.toArray(children);
    const slottable = childrenArray.find(isSlottable);

    if (slottable) {
      const newElement = slottable.props.children;
      const newChildren = childrenArray.map(child => {
        if (child === slottable) {
          if (React.Children.count(newElement) > 1) {
            return React.Children.only(null);
          }
          if (
            React.isValidElement<{ children?: React.ReactNode }>(newElement)
          ) {
            return newElement.props.children ?? null;
          }
          return null;
        }
        return child;
      });

      return (
        <SlotClone ref={forwardedRef} {...slotProps}>
          {React.isValidElement(newElement) ? newElement : null}
        </SlotClone>
      );
    }

    return (
      <SlotClone ref={forwardedRef} {...slotProps}>
        {children}
      </SlotClone>
    );
  },
);

Slot.displayName = 'Slot';

const SlotClone = React.forwardRef<HTMLElement, SlotProps>(
  ({ children, ...slotProps }, forwardedRef) => {
    if (React.isValidElement(children)) {
      const child = children as React.ReactElement;
      const childProps = child.props as Record<string, unknown>;
      const mergedProps = mergeProps(
        slotProps as Record<string, unknown>,
        childProps,
      );
      return React.cloneElement(child, {
        ...mergedProps,
        // Intentionally do not read child.props.ref or element.ref to
        // avoid React 19 warnings about accessing element.ref.
        // We only forward the outer ref.
        // @ts-expect-error - ref is not part of props typings
        ref: forwardedRef ?? undefined,
      });
    }

    if (React.Children.count(children) > 1) {
      return React.Children.only(null);
    }

    return null;
  },
);

SlotClone.displayName = 'SlotClone';

const Slottable: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>{children}</>
);

function isSlottable(
  child: React.ReactNode,
): child is React.ReactElement<{ children?: React.ReactNode }> {
  return React.isValidElement(child) && child.type === Slottable;
}

function mergeProps(
  slotProps: Record<string, unknown>,
  childProps: Record<string, unknown>,
) {
  const overrideProps: Record<string, unknown> = { ...childProps };

  for (const propName in childProps) {
    // Never read or merge the `ref` prop from the child – accessing it
    // triggers React 19's element.ref warning and we only preserve the
    // outer ref instead.
    if (propName === 'ref') continue;

    const slotPropValue = slotProps[propName];
    const childPropValue = childProps[propName];
    const isHandler = /^on[A-Z]/.test(propName);

    if (isHandler) {
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args: unknown[]) => {
          (childPropValue as (...args: unknown[]) => void)(...args);
          (slotPropValue as (...args: unknown[]) => void)(...args);
        };
      } else if (slotPropValue) {
        overrideProps[propName] = slotPropValue;
      }
    } else if (propName === 'style') {
      overrideProps[propName] = {
        ...(slotPropValue as object),
        ...(childPropValue as object),
      };
    } else if (propName === 'className') {
      overrideProps[propName] = [slotPropValue, childPropValue]
        .filter(Boolean)
        .join(' ');
    }
  }

  return { ...slotProps, ...overrideProps };
}

function createSlot(displayName?: string) {
  const CreatedSlot = React.forwardRef<HTMLElement, SlotProps>(
    (props, ref) => <Slot {...props} ref={ref} />,
  );
  CreatedSlot.displayName = displayName ?? 'Slot';
  return CreatedSlot;
}

function createSlottable<TProps extends { children?: React.ReactNode }>(
  Component: React.ComponentType<TProps>,
) {
  const SlottableComponent: React.FC<TProps> = props => {
    const { children, ...rest } = props;
    return (
      <Slottable>
        <Component {...(rest as TProps)}>{children}</Component>
      </Slottable>
    );
  };

  SlottableComponent.displayName =
    Component.displayName || Component.name || 'Slottable';

  return SlottableComponent;
}

const Root = Slot;

export { Slot, Slottable, Root, createSlot, createSlottable };

